import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

const Login = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [regData, setRegData] = useState({
    name: "",
    email: "",
    department: "",
    nickname: "",
    esc: "",
    location: "",
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const navigate = useNavigate();

  // Email/password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/login`,
        { email, password },
        { validateStatus: () => true }
      );
      if (res.status === 403 && res.data.error) return setError(res.data.error);
      if (res.status !== 200)
        return setError(res.data.error || "Login failed.");

      const userData = {
        name: res.data.name,
        offices: res.data.offices,
        role: res.data.role,
        employeeId: res.data.employeeId,
        email: res.data.email,
        location: res.data.location,
        token: res.data.token,
      };

      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed.");
    }
  };

  // Google login handler
  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const googleEmail = decoded.email; // ✅ now stored in a variable

      const res = await axios.post(`${API_URL}/login/google-login`, {
        token: credentialResponse.credential,
      });

      const userData = {
        name: res.data.name,
        role: res.data.role,
        employeeId: res.data.employeeId,
        email: res.data.email,
        location: res.data.location,
        token: res.data.token,
        offices: res.data.offices
      };

      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      // ✅ Stay on the same page (no redirect)
    } catch (err) {
      console.error("Google login error:", err);
      setError("Google login failed.");
    }
  };

  const handleRegistration = async () => {
    try {
      await axios.post(`${API_URL}/api/register`, regData);
      alert("Account created successfully!");
      setModalOpen(false);
      setRegData({
        name: "",
        email: "",
        department: "",
        nickname: "",
        esc: "",
        location: "",
      });
    } catch (err) {
      console.error("Error creating account:", err);
      alert("Error creating account.");
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center vh-100"
      style={{
        backgroundImage: "url('/background.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="card p-4 shadow-lg" style={{ width: "360px" }}>
        <img
          src="/logo.png"
          alt="Attendance App Logo"
          className="mx-auto d-block mb-3"
          style={{ height: "50px" }}
        />
        <h3 className="text-center mb-3">Employee Login</h3>
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Email/Password Form */}
        <form onSubmit={handleLogin}>
          <div className="mb-3">
            <label className="form-label">
              <b>Email</b>
            </label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              <b>Password</b>
            </label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword((p) => !p)}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary w-100">
            Login
          </button>
        </form>

        {/* Google Login Button */}
        <div className="text-center mt-3">
          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => setError("Google Sign-In failed.")}
          />
        </div>

        {/* Registration Links */}
        <div className="text-center mt-3">
          <a
            href="https://prakalp2.mydashboard.site/register"
            target="_blank"
            rel="noreferrer"
          >
            Go to Registration Page
          </a>
          <br />
          <button className="btn btn-link" onClick={() => setModalOpen(true)}>
            Create an Account
          </button>
        </div>
      </div>

      {/* Registration Modal */}
      {modalOpen && (
        <div className="modal show fade d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Account</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalOpen(false)}
                />
              </div>
              <div className="modal-body">
                {[
                  "name",
                  "email",
                  "department",
                  "nickname",
                  "esc",
                  "location",
                ].map((field) => (
                  <div className="mb-2" key={field}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={
                        field.charAt(0).toUpperCase() + field.slice(1)
                      }
                      value={regData[field]}
                      onChange={(e) =>
                        setRegData((d) => ({ ...d, [field]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleRegistration}
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
